import _ from 'lodash';
import { useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import type { PermissionSystem } from '@adflow/shared/util-permissions';

export function useCheckSuperPermsOnSuperuserChange<
	A extends string,
	P extends string,
>(
	// I can't make this type-safe with all the different forms
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	formMethods: UseFormReturn<any>,
	permissionSystem: PermissionSystem<A, P>
) {
	const { getValues, setValue, formState, watch } = formMethods;

	const superuser: boolean | undefined = watch('superuser');

	// delay returned superuser value
	// so value changes and readonly changes happen at the same time
	const [superuserState, setSuperuserState] = useState(superuser);

	const superPermKeys = permissionSystem.superPerms;
	const superPermFormKeys = superPermKeys.map(
		([P, A]) => `permissions.${P}.${A}`
	);
	const superPermFormValues = getValues(superPermFormKeys) as (
		boolean | undefined
	)[];

	const shouldDirty = !!formState.dirtyFields?.superuser;

	useEffect(() => {
		setSuperuserState(superuser);
		if (superuser && _.some(superPermFormValues, (x) => !x)) {
			for (const p of superPermFormKeys) {
				setValue(p, true, {
					shouldDirty,
				});
			}
		}
		if (!superuser && _.some(superPermFormValues)) {
			for (const p of superPermFormKeys) {
				setValue(p, false, {
					shouldDirty,
				});
			}
		}
	}, [
		setValue,
		superPermFormKeys,
		superPermFormValues,
		shouldDirty,
		superuser,
	]);

	return superuserState;
}
